import { useMemo } from "react";
import { useUser } from "@/features/user/hooks/useUser";
import {
  ChatStepId,
  type ChatProfile,
  type ChatScenario,
  type ChatStep,
  type OnboardingFacts,
} from "../types/chat";
import { chatScenario } from "../engine/scenarios";
import { getDoneSteps, selectNextStep } from "../engine/selectNextStep";
import { useOnboardingFacts } from "./useOnboardingFacts";

interface UseChatStepReturn {
  scenario: ChatScenario;
  facts: OnboardingFacts | null;
  profile: ChatProfile;
  step: ChatStep | null;
  doneSteps: ChatStep[];
  isLoading: boolean;
}

const SCORING_STEPS: readonly ChatStepId[] = [
  ChatStepId.BcScoring,
  ChatStepId.PreliminaryOffer,
];

// The current step of the user's chat scenario, recomputed from backend data
// on every render: leaving mid-flow, reloading or switching device resumes at
// the same step.
export const useChatStep = (): UseChatStepReturn => {
  const scenario = chatScenario;
  const { user } = useUser();

  const { facts, isLoading } = useOnboardingFacts({
    fetchScoring: scenario.steps.some((id) => SCORING_STEPS.includes(id)),
  });

  const profile = useMemo<ChatProfile>(
    () => ({
      email: user?.email || null,
      cnp: user?.cnp || null,
      declaredIncome: user?.declaredIncome ?? null,
    }),
    [user?.email, user?.cnp, user?.declaredIncome],
  );

  return useMemo(
    () => ({
      scenario,
      facts,
      profile,
      step: facts ? selectNextStep(scenario, facts) : null,
      doneSteps: facts ? getDoneSteps(scenario, facts) : [],
      isLoading,
    }),
    [scenario, facts, profile, isLoading],
  );
};
